import React, { useEffect, useMemo, useState } from 'react';
import { Card, Select, Space, Typography } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import QaqcPipeWelderCreate from './QaqcPipeWelderCreate';
import type { QaqcPipeWelder } from '../../../../types';
import { fetchSqlData } from '../../../../services/api';
import { useGetQaqcPipeWelder, useGetPersonnel } from '../../../../hooks/genericHooks';

const QaqcPipeWelderEdit: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: { record?: QaqcPipeWelder } };
  const params = useParams();
  const [record, setRecord] = useState<QaqcPipeWelder | undefined>(state?.record);
  const { data: weldersResp } = useGetQaqcPipeWelder();
  const { data: personnelResp } = useGetPersonnel();

  useEffect(() => {
    const load = async () => {
      if (record) return;
      const id = params.id ? Number(params.id) : NaN;
      if (!Number.isFinite(id)) return;
      try {
        const res = await fetchSqlData('qaqc_pipe_welder', 'id', id);
        const row = Array.isArray(res?.data) ? res.data[0] : (Array.isArray(res) ? res[0] : undefined);
        if (row) setRecord(row as QaqcPipeWelder);
      } catch {}
    };
    load();
  }, [params.id]);

  const goBack = () => navigate(-1);
  const welderOptions = useMemo(() => {
    const rows: QaqcPipeWelder[] = (weldersResp?.data as any) || [];
    const persList: any[] = (personnelResp?.data as any) || [];
    return rows.map(w => {
      const p = persList.find(pp => pp.pers_id === w.personnel_id);
      const label = p ? `${w.welder_name} — ${p.first_name} ${p.last_name}` : w.welder_name;
      return { value: w.id, label, rec: w };
    }).sort((a,b)=>String(a.label).localeCompare(String(b.label)));
  }, [weldersResp, personnelResp]);

  const missingId = !record && !(params.id && Number.isFinite(Number(params.id)));

  return (
    <>
      <style>{`
        /* Remove gray bg and gray text from disabled inputs/selects only in this page */
        .welder-no-shade .ant-input[disabled],
        .welder-no-shade .ant-input-affix-wrapper-disabled,
        .welder-no-shade .ant-select-disabled .ant-select-selector {
          background-color: transparent !important;
          color: inherit !important;
        }
        .welder-no-shade .ant-select-disabled .ant-select-selection-item,
        .welder-no-shade .ant-select-disabled .ant-select-arrow {
          color: inherit !important;
        }
      `}</style>
      <div className="welder-no-shade">
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {missingId && (
            <Card size="small" title={<Typography.Text strong>Select a welder to edit</Typography.Text>}>
              <Select
                showSearch
                allowClear
                placeholder="Search welder or personnel"
                style={{ width: 420 }}
                options={welderOptions}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label || '').toLowerCase();
                  const tokens = input.trim().toLowerCase().split(/\s+/).filter(Boolean);
                  return tokens.every(t => label.includes(t));
                }}
                onChange={(val, opt: any) => {
                  const id = Number(val);
                  const rec = opt?.rec as QaqcPipeWelder | undefined;
                  if (Number.isFinite(id)) {
                    if (rec) setRecord(rec);
                    navigate(`/qaqc/piping/tools/welders/edit/${id}`, { state: { record: rec } });
                  }
                }}
              />
            </Card>
          )}
          <QaqcPipeWelderCreate
            mode="edit"
            initialValues={record}
            onSaved={() => {
              // navigate back to list; list will auto-refresh via event emitted in create component
              goBack();
            }}
            onCancel={goBack}
            onBack={goBack}
          />
        </Space>
      </div>
    </>
  );
};

export default QaqcPipeWelderEdit;
